import { useEffect, useState, useRef, createContext, useContext, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Meta Pixel: the base code in index.html fires PageView on the initial load; fire exactly once per SPA navigation after that.
let lastPixelPath = typeof window !== "undefined" ? window.location.pathname : "";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (pathname === lastPixelPath) return;
    lastPixelPath = pathname;
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  }, [pathname]);
  useEffect(() => {
    if (!hash || hash.includes("session_id=")) { window.scrollTo(0, 0); return undefined; }
    // Anchor links (e.g. /#newsletter): the target renders after data loads and content above it keeps
    // settling (images, feed), so keep re-aligning for a few seconds unless the reader scrolls themselves.
    const id = hash.slice(1);
    let ticks = 0;
    let cancelled = false;
    const cancel = () => { cancelled = true; };
    window.addEventListener("wheel", cancel, { passive: true });
    window.addEventListener("touchstart", cancel, { passive: true });
    const timer = setInterval(() => {
      ticks += 1;
      const el = document.getElementById(id);
      if (cancelled || ticks > 40) { clearInterval(timer); return; }
      if (el && Math.abs(el.getBoundingClientRect().top) > 4) el.scrollIntoView({ behavior: "auto", block: "start" });
    }, 100);
    return () => { clearInterval(timer); window.removeEventListener("wheel", cancel); window.removeEventListener("touchstart", cancel); };
  }, [pathname, hash]);
  return null;
};

// Components
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ArticlePage from "./pages/ArticlePage";
import CategoryPage from "./pages/CategoryPage";

// Event-microsite footers only render on BIF / India 2030 hosts or their landing pages.
const BifFooter = lazy(() => import("./components/BifFooter"));
const I2030Footer = lazy(() => import("./components/I2030Footer"));

// Route-level code splitting: visitors only download the chunk for the page they open.
// Admin/editor/social tooling, event microsites and long-tail pages stay out of the public bundle.
const PersonPage = lazy(() => import("./pages/PersonPage"));
const EventPage = lazy(() => import("./pages/EventPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LaunchingSoon = lazy(() => import("./pages/LaunchingSoon"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const DisclaimerPage = lazy(() => import("./pages/DisclaimerPage"));
const TermsOfUsePage = lazy(() => import("./pages/TermsOfUsePage"));
const BuiltIndiaForumPage = lazy(() => import("./pages/BuiltIndiaForumPage"));
const FlagshipIPsPage = lazy(() => import("./pages/FlagshipIPsPage"));
const India2030ConclavePage = lazy(() => import("./pages/India2030ConclavePage"));
const SponsorReportPage = lazy(() => import("./pages/SponsorReportPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const SubmitStoryPage = lazy(() => import("./pages/SubmitStoryPage"));
const EditorialStandardsPage = lazy(() => import("./pages/EditorialStandardsPage"));
const MediaKitPage = lazy(() => import("./pages/MediaKitPage"));

// Quiet fallback while a route chunk downloads: reserves height so the footer does not jump.
// Full viewport height so the footer is never on screen while a lazy route loads (a 60vh placeholder caused CLS 0.32).
const RouteFallback = () => <div className="min-h-screen" aria-hidden="true" />;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);
const SESSION_FLAG = 'pwm_has_session';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      localStorage.setItem(SESSION_FLAG, '1');
    } catch (error) {
      setUser(null);
      localStorage.removeItem(SESSION_FLAG);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Anonymous readers never had a session: skip the probe so public pages do not log a 401.
    // The probe always runs on /admin and for browsers that have signed in before (flag set on login).
    if (!window.location.pathname.startsWith('/admin') && localStorage.getItem(SESSION_FLAG) !== '1') {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem(SESSION_FLAG);
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          setUser(response.data);
          localStorage.setItem(SESSION_FLAG, '1');
          navigate('/admin', { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("Auth error:", error);
          toast.error("Authentication failed");
          navigate('/', { replace: true });
        }
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-neutral-950 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-neutral-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-950 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// ============================================================
// HOST-AWARE MODE
// Detects when the app is being served from a standalone event
// domain (BIF or India 2030) and swaps in the corresponding
// landing page + minimal footer, hiding the People Who Matter chrome.
// Configure via env vars:
//   REACT_APP_BIF_HOSTS="builtindiaforum.com,builtindiaforum.in,..."
//   REACT_APP_I2030_HOSTS="india2030leadershipconclave.com,..."
// ============================================================
const BIF_HOSTS = (process.env.REACT_APP_BIF_HOSTS || "builtindiaforum.com,builtindiaforum.in,www.builtindiaforum.com,www.builtindiaforum.in")
  .split(",")
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

const I2030_HOSTS = (process.env.REACT_APP_I2030_HOSTS || "india2030leadershipconclave.com,www.india2030leadershipconclave.com,india2030leadershipconclave.in,www.india2030leadershipconclave.in")
  .split(",")
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

const isBifHost = () => {
  if (typeof window === "undefined") return false;
  // Manual override for testing on preview: visit ?bif=1
  if (window.location.search.includes("bif=1")) return true;
  return BIF_HOSTS.includes(window.location.hostname.toLowerCase());
};

const isI2030Host = () => {
  if (typeof window === "undefined") return false;
  // Force override: set REACT_APP_I2030_FORCE=1 on a dedicated I2030 deployment
  // so any hostname (including the default preview URL) renders the I2030 page.
  if (process.env.REACT_APP_I2030_FORCE === "1") return true;
  // Manual override for testing on preview: visit ?i2030=1
  if (window.location.search.includes("i2030=1")) return true;
  return I2030_HOSTS.includes(window.location.hostname.toLowerCase());
};

// App Router
function AppRouter() {
  const location = useLocation();
  const bifMode = isBifHost();
  const i2030Mode = isI2030Host();
  
  // Check URL fragment for session_id synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Choose the root element based on host
  let rootElement = <HomePage />;
  if (bifMode) rootElement = <BuiltIndiaForumPage />;
  else if (i2030Mode) rootElement = <India2030ConclavePage />;

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* When served from a BIF host, the root URL shows the Built India Forum page.
          When served from an India 2030 host, the root URL shows the I2030 Conclave page.
          On peoplewhomatter.in (and preview), the root shows the full HomePage. */}
      <Route path="/" element={rootElement} />
      <Route path="/home" element={<Navigate to={{ pathname: "/", hash: location.hash }} replace />} />
      <Route path="/launching-soon" element={<LaunchingSoon />} />
      <Route path="/article/:slug" element={<ArticlePage />} />
      <Route path="/person/:slug" element={<PersonPage />} />
      <Route path="/event/:slug" element={<EventPage />} />
      <Route path="/category/:category" element={<CategoryPage />} />
      <Route path="/latest" element={<Navigate to="/category/latest" replace />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/terms" element={<TermsOfUsePage />} />
      <Route path="/built-india-forum" element={<BuiltIndiaForumPage />} />
      <Route path="/flagship-ips" element={<FlagshipIPsPage />} />
      <Route path="/india-2030-leadership-conclave" element={<India2030ConclavePage />} />
      <Route path="/sitemap" element={<SitemapPage />} />
      <Route path="/report/:token" element={<SponsorReportPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/submit-story" element={<SubmitStoryPage />} />
      <Route path="/editorial-standards" element={<EditorialStandardsPage />} />
      <Route path="/media-kit" element={<MediaKitPage />} />
      <Route path="*" element={<NotFoundPage />} />
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
    </Routes>
    </Suspense>
  );
}

// Main Layout
const MainLayout = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isReport = location.pathname.startsWith('/report/');
  const bifMode = isBifHost() || location.pathname === '/built-india-forum';
  const i2030Mode = isI2030Host() || location.pathname === '/india-2030-leadership-conclave';
  const isLaunchingSoon = location.pathname === '/launching-soon';

  if (isAdmin || isLaunchingSoon || isReport) {
    return children;
  }

  // Standalone Built India Forum site: no PWM nav, only BIF-specific footer
  if (bifMode) {
    return (
      <>
        <main>{children}</main>
        <Suspense fallback={null}><BifFooter /></Suspense>
      </>
    );
  }

  // Standalone India 2030 Leadership Conclave site: no PWM nav, I2030-specific footer
  if (i2030Mode) {
    return (
      <>
        <main>{children}</main>
        <Suspense fallback={null}><I2030Footer /></Suspense>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <main>{children}</main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Toaster position="top-right" />
          <MainLayout>
            <AppRouter />
          </MainLayout>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
